import { Search, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState, Skeleton } from "../components/ui";
import { useAssetCustomers } from "../data/assets-platform-data";
import { useCustomers } from "../data/platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";

function tone(value: string): "success" | "warning" | "danger" | "neutral" {
  if (value === "ACTIVE" || value === "ATIVO") return "success";
  if (value === "WAITING_CARENCY" || value === "CARÊNCIA") return "warning";
  if (value === "SUSPENDED" || value === "SUSPENSO" || value === "OVERDUE") {
    return "danger";
  }
  return "neutral";
}

export function CustomersPage() {
  const { hasModule, tenant } = useTenantRuntime();
  const isFlow = tenant.product.commercialProductCode === "EVA_FLOW";
  const assetVertical = hasModule("assets");
  const familyQuery = useCustomers();
  const assetQuery = useAssetCustomers();
  const query = assetVertical ? assetQuery : familyQuery;

  return (
    <Card
      title={assetVertical ? "Clientes e empresas" : "Base de famílias"}
      description={
        isFlow
          ? "Pessoas e empresas vinculadas a contratos de qualquer produto ou serviço."
          : assetVertical
            ? "Compradores, locatários, motoristas e pessoas jurídicas em uma única base."
            : "Uma pessoa existe uma única vez e participa dos produtos por função."
      }
      action={
        <Link to={assetVertical ? "/operacoes/nova?tipo=counterparty" : "/onboarding"}>
          <Button>
            <UserPlus size={16} /> Novo cliente
          </Button>
        </Link>
      }
    >
      <div className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            aria-label="Buscar clientes"
            placeholder={
              assetVertical
                ? "Nome, CPF, CNPJ, contrato ou ativo"
                : "Nome, CPF, telefone ou contrato"
            }
          />
        </div>
        <select aria-label="Filtrar situação">
          <option>Todos os status</option>
          <option>Ativo</option>
          <option>Vencido</option>
          <option>Suspenso</option>
        </select>
      </div>

      {query.isLoading && <Skeleton className="table-skeleton" />}
      {query.error && (
        <EmptyState title="Não foi possível carregar" description={query.error.message} />
      )}

      {!query.isLoading && !query.error && assetVertical && (
        <DataTable
          rows={assetQuery.data ?? []}
          getKey={(customer) => customer.id}
          columns={[
            {
              key: "name",
              header: "Cliente",
              render: (customer) => (
                <Link className="strong-link" to={`/clientes/${customer.id}`}>
                  {customer.name}
                  <small>
                    {customer.documentType} · {customer.document}
                  </small>
                </Link>
              ),
            },
            {
              key: "contact",
              header: "Contato",
              render: (customer) => customer.contact,
            },
            {
              key: "relationship",
              header: "Relação",
              render: (customer) => customer.relationship,
            },
            {
              key: "agreements",
              header: "Contratos ativos",
              render: (customer) => customer.activeAgreements,
            },
            {
              key: "balance",
              header: "Saldo aberto",
              render: (customer) => customer.openBalance,
            },
            {
              key: "status",
              header: "Situação",
              render: (customer) => <Badge tone={tone(customer.status)}>{customer.status}</Badge>,
            },
          ]}
        />
      )}

      {!query.isLoading && !query.error && !assetVertical && (
        <DataTable
          rows={familyQuery.data ?? []}
          getKey={(customer) => customer.id}
          columns={[
            {
              key: "name",
              header: "Família",
              render: (customer) => (
                <Link className="strong-link" to={`/clientes/${customer.id}`}>
                  {customer.name}
                  <small>{customer.holder}</small>
                </Link>
              ),
            },
            {
              key: "phone",
              header: "Contato",
              render: (customer) => customer.phone,
            },
            {
              key: "funeral",
              header: "Funeral",
              render: (customer) => <Badge tone={tone(customer.funeral)}>{customer.funeral}</Badge>,
            },
            {
              key: "telemedicine",
              header: "Telemedicina",
              render: (customer) => (
                <Badge tone={tone(customer.telemedicine)}>{customer.telemedicine}</Badge>
              ),
            },
            {
              key: "seller",
              header: "Vendedor",
              render: (customer) => customer.seller,
            },
            {
              key: "balance",
              header: "Financeiro",
              render: (customer) => (
                <Badge tone={customer.balance === "Em dia" ? "success" : "danger"}>
                  {customer.balance}
                </Badge>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}
